import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { ModeKey, modes } from "./modes";
import { useModeSync } from "./useModeSync";
import { useGlobalSettings } from "../context/GlobalSettingsContext";
import {
  clearTransientState,
  prepareSessionForResume,
  saveSession,
  SessionHistoryEntry,
} from "../utils/localStorage";
import { SimulationFormParams } from "../types/forms";

interface ModeContextValue {
  mode: ModeKey | null;
  setMode: (m: ModeKey | null) => void;
  activeSessionId: string | null;
  resumeKey: number;
  resumeSession: (session: SessionHistoryEntry) => void;
  exitToExplore: () => void;
  /** Auto-save: create or update the active session in history */
  persistSession: (simulations: SimulationFormParams[]) => void;
}

const ModeContext = createContext<ModeContextValue | undefined>(undefined);

interface ModeProviderProps {
  children: React.ReactNode;
  onClearSimulations?: () => void;
}

export const ModeProvider: React.FC<ModeProviderProps> = ({
  children,
  onClearSimulations,
}) => {
  // Always start on the explore page (mode = null)
  const [mode, setMode] = useState<ModeKey | null>(null);
  const [prevMode, setPrevMode] = useState<ModeKey | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [resumeKey, setResumeKey] = useState(0);
  const { setModeControl } = useGlobalSettings();

  // Clear any leftover transient hydration state on startup
  useEffect(() => {
    clearTransientState();
  }, []);

  // Sync with external systems when mode changes
  useModeSync(mode);

  // Apply mode-controlled settings when mode changes
  useEffect(() => {
    if (mode) {
      const modeDefinition = modes[mode];
      if (modeDefinition?.controlledSettings) {
        setModeControl(mode, modeDefinition.controlledSettings);
      } else {
        setModeControl(mode);
      }
    } else {
      // Clear all mode controls when exiting mode
      setModeControl(null);
    }
  }, [mode, setModeControl]);

  // Clear simulations when mode is set to null
  useEffect(() => {
    if (mode === null && prevMode !== null && onClearSimulations) {
      onClearSimulations();
    }
    setPrevMode(mode);
  }, [mode, prevMode, onClearSimulations]);

  /**
   * Resume a session from history: write hydration state for
   * useSimulationFormModel, set mode, and bump resumeKey to remount.
   */
  const resumeSession = useCallback((session: SessionHistoryEntry) => {
    prepareSessionForResume(session);
    setActiveSessionId(session.id);
    setMode(session.mode);
    setResumeKey((k) => k + 1);
  }, []);

  /**
   * Exit to the explore page. The session is already saved in history
   * (auto-saved on every form submit), so just clear transient state and
   * reset mode.
   */
  const exitToExplore = useCallback(() => {
    clearTransientState();
    setActiveSessionId(null);
    setMode(null);
  }, []);

  /**
   * Auto-save: create or update the active session in history.
   * Called on every form submit / duplicate / delete.
   */
  const persistSession = useCallback(
    (simulations: SimulationFormParams[]) => {
      if (!mode) return;

      // Only persist simulations that have been run (have resultIndex)
      const simsWithResults = simulations.filter(
        (s) => s.resultIndex !== undefined,
      );
      if (simsWithResults.length === 0) return;

      const newId = saveSession(activeSessionId, mode, simsWithResults);
      if (newId !== activeSessionId) {
        setActiveSessionId(newId);
      }
    },
    [mode, activeSessionId],
  );

  return (
    <ModeContext.Provider
      value={{
        mode,
        setMode,
        activeSessionId,
        resumeKey,
        resumeSession,
        exitToExplore,
        persistSession,
      }}
    >
      {children}
    </ModeContext.Provider>
  );
};

export const useMode = () => {
  const ctx = useContext(ModeContext);
  if (!ctx) throw new Error("useMode must be used within a ModeProvider");
  return ctx;
};
